import { apiClient } from '@/services/apiClient'
import type {
  ApiResponse,
  AuthResponse,
  OtpRequest,
  UpdateProfileRequest,
  User,
} from '@/interfaces'

export const authApi = {

  /**
   * Send Unified OTP (auto-detects existing vs new user on backend)
   */
  async sendUnifiedOtp(data: { mobile_no: string }): Promise<ApiResponse<{ is_existing_user: boolean }>> {
    const response = await apiClient.post('customer/unified_otp', data)
    return response.data
  },


  /**
   * Send OTP to phone/email
   * Legacy endpoint: customer/otp/create
   * Requires: mobile_no and account_exists
   */
  async generateOTP(data: OtpRequest & { account_exists?: string }): Promise<ApiResponse> {
    const payload = {
      mobile_no: data.mobile_no,
      account_exists: data.account_exists || 'Yes', // Default to 'Yes' for login flow
    }
    const response = await apiClient.post('customer/otp/create', payload)
    return response.data
  },

  /**
   * Check OTP validity only (does not generate token)
   * Legacy endpoint: customer/otp/check_validity
   */
  async checkOtpValidity(data: { mobile_no: string; otp: string }): Promise<ApiResponse> {
    const response = await apiClient.post('customer/otp/check_validity', {
      mobile_no: data.mobile_no,
      otp: data.otp,
    })
    return response.data
  },

  /**
   * Verify OTP and get access token (for login)
   * Legacy flow:
   * 1. Check OTP validity (customer/otp/check_validity)
   * 2. Generate token (customer/generate_token)
   */
  async verifyOTP(data: OtpRequest & { otp: string }): Promise<ApiResponse<AuthResponse>> {
    // First check OTP validity
    const otpCheckResponse = await apiClient.post('customer/otp/check_validity', {
      mobile_no: data.mobile_no,
      otp: data.otp,
    })

    // If OTP check failed, return the error
    if (otpCheckResponse.data?.error) {
      return otpCheckResponse.data
    }

    // Then generate token
    const tokenResponse = await apiClient.post('customer/generate_token', {
      mobile_no: data.mobile_no,
      refresh_required: true,
    })

    // Legacy: response.data.error means error, otherwise response.data contains the token data
    const responseData = tokenResponse.data

    // Check if response is a plain string error (backend sometimes returns error as string)
    if (typeof responseData === 'string') {
      // If it's just a string, it's likely an error message
      if (responseData.trim().length > 0 && !responseData.includes('customer_access_token')) {
        return { error: responseData.trim() }
      }
    }

    // Check if generate_token returned an error (even with 200 status)
    // Error can be a string or an object
    if (responseData?.error) {
      const errorMsg =
        typeof responseData.error === 'string'
          ? responseData.error
          : responseData.error.message || JSON.stringify(responseData.error)
      return { error: errorMsg }
    }

    // Check if response is empty or invalid
    if (
      !responseData ||
      (typeof responseData === 'object' && Object.keys(responseData).length === 0)
    ) {
      return { error: 'Empty response from server' }
    }

    // Check for Laravel error structure (message field without token)
    if (responseData?.message && !responseData?.customer_access_token) {
      return { error: responseData.message }
    }

    // Return the token response
    // Legacy format: response.data contains customer_access_token and user_data directly
    return responseData
  },

  /**
   * Refresh access token using refresh token
   * Legacy endpoint: customer/refresh_token
   */
  async refreshToken(): Promise<ApiResponse<AuthResponse>> {
    const response = await apiClient.post('customer/refresh_token', {})
    // Legacy format: response.data contains customer_access_token and user_data directly
    return response.data
  },

  /**
   * Logout - invalidate refresh token
   */
  async logout(): Promise<ApiResponse> {
    const response = await apiClient.post('customer/logout')
    return response.data
  },

  /**
   * Check if customer mobile exists
   * This endpoint also creates OTP if account exists (legacy behavior)
   */
  async checkCustomerMobile(data: OtpRequest): Promise<ApiResponse> {
    const response = await apiClient.post('customer/check_customer_mobile', data)
    return response.data
  },

  /**
   * Resend OTP
   * Legacy endpoint: customer/otp/resend
   */
  async resendOTP(data: OtpRequest): Promise<ApiResponse> {
    const response = await apiClient.post('customer/otp/resend', data)
    return response.data
  },

  /**
   * Check customer info validity and generate token (for registration)
   * Legacy endpoint: customer/check_customer_info_validity
   * This endpoint generates the token after OTP verification
   */
  async checkCustomerInfoValidity(data: {
    mobile_no: string
    first_name: string
    last_name: string
    email?: string
    refresh_required: boolean
  }): Promise<ApiResponse<AuthResponse>> {
    const response = await apiClient.post('customer/check_customer_info_validity', data)
    return response.data
  },

  async getCurrentUser(): Promise<ApiResponse<User>> {
    const response = await apiClient.post('customer/get_current_user', {})
    return response.data
  },

  async updateCurrentUser(data: UpdateProfileRequest): Promise<ApiResponse<User>> {
    const response = await apiClient.post('customer/update_current_user', data)
    return response.data
  },
}
